import { LitElement, html, css, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

import "./header";
import { DEVICE } from "../core/device";

type ShellMode = "enterprise" | "left" | "center" | "full";

@customElement("app-shell")
export class AppShell extends LitElement {
  /**
   * mode:
   * - enterprise (DEFAULT): horizontal centered column, vertical top
   * - left: top-left flow (no forced column width)
   * - center: login style (vertical+horizontal center)
   * - full: full width/height for heavy tables
   */
  @property({ type: String }) mode: ShellMode = "enterprise";
  @property({ type: Boolean }) showHeader = true;
  @property({ type: Boolean }) fullHeight = false;
  @property({ type: Boolean }) fullHeightMobile = false;

  static styles = css`
    :host {
      display: block;
      height: 95vh;
      overflow: hidden;
      background: #fff;
      color: #111;
      font-family: var(--font-corporate);
      box-sizing: border-box;
      --header-height: 120px;
    }

    :host([no-header]){
      --header-height: 0px;
    }

    .header-spacer {
      height: 120px; /* dein header host height */
    }

    .body-content {
      position: relative;
      height: calc(100vh - var(--header-height));
      box-sizing: border-box;
      padding-top: 10px; /* exakt 10px wie vereinbart */
      padding-bottom: 55px; /* footer reserve */
      overflow: hidden; /* NIE die ganze Seite scrollen */
    }

    .body-content.full-height {
      height: 100vh;
    }

    .body-content.full-height-mobile {
      height: 60vh;
    }

    /* Full width wrapper */
    .container {
      width: 100%;
      max-width: 100%;
      margin: 0;
      padding: 0 15px;
      box-sizing: border-box;
    }

    /* Ultra-wide cap (für Enterprise angenehme Lesebreite) */
    .container-inner {
      width: 100%;
      max-width: 1800px;
      margin: 0 auto;
      box-sizing: border-box;
    }

    /* --- Layout Areas --- */
    .area {
      height: calc(100vh - 150px - 55px - 10px);
      box-sizing: border-box;
      overflow: hidden;
    }

    /* Enterprise = horizontal center, vertical top */
    .enterprise-area {
      display: flex;
      align-items: flex-start;   /* TOP */
      justify-content: flex-start;
    }

    .enterprise-column {
      width: 100%;
      max-width: 1200px;         /* zentrale Lesebreite */
      margin: 0 auto;            /* horizontal center */
      box-sizing: border-box;
      overflow: auto;            /* jede Seite/Column kann selbst scrollen */
      max-height: 100%;
    }

    /* Left = top-left, full width content */
    .left-area {
      display: block;
      overflow: auto;
      max-height: 100%;
    }

    /* Center = login style (vertikal+horizontal) */
    .center-area {
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: auto;
      max-height: 100%;
    }

    /* Full = full width/height (tanks/tables etc.) */
    .full-area {
      display: block;
      overflow: hidden;
      height: 100%;
    }

    footer {
      position: fixed;
      bottom: 0;
      left: 0;
      width: 100%;
      padding-left: 15px;
      background: #fff;
      z-index: 8000;
      border-top: 1px solid rgba(0, 0, 0, 0.08);
    }

    footer p {
      margin: 10px 0;
      font-size: 12px;
      color: rgba(0, 0, 0, 0.75);
      font-family: var(--font-corporate);
    }
  `;

  render() {
    const year = new Date().getFullYear();

    return html`
      ${this.showHeader ? html`<app-header class="header-spacer"></app-header>` : nothing}

      <main class="body-content ${this.fullHeight ? "full-height" : ""} ${this.fullHeightMobile ? "full-height-mobile" : ""}">
        <div class="container">
          <div class="container-inner">
            ${this.mode === "center"
              ? html`<div class="area center-area"><slot></slot></div>`
              : this.mode === "full"
              ? html`<div class="area full-area"><slot></slot></div>`
              : this.mode === "left"
              ? html`<div class="area left-area"><slot></slot></div>`
              : html`
                  <div class="area enterprise-area">
                    <div class="enterprise-column">
                      <slot></slot>
                    </div>
                  </div>
                `}
          </div>
        </div>
      </main>

      <footer>
        <p>&copy; ${year} - Ilzhöfer - Technik</p>
      </footer>
    `;
  }
}
